import { Box,LinearProgress, Typography, useTheme } from "@mui/material";
import { Clock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useOutletContext } from "react-router-dom";
import { BigCarouselCardProps } from "./BigCarouselCard.types";
import {
  BigCarouselCardStyled,
  ContainerTitleSubTitle,
  StyledBoxTopLeft,
  StyledBoxTopRight,
  StyledTitle,
  StyledSubtitle
} from "./BigCarouselCardRestyle.style";
import { CardCta } from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CardTypeRestyle } from "../../components/CardType/CardTypeRestyle.component";
import { StatusBoxesContainer } from "../../components/CarouselCardRestyle/CarouselCard.style";
import { ENROLL_TYPE, LEVEL, STATUS_LO } from "../../consts";
import {
  useButtonStatusMap,
  usePlayResumeVideo
} from "../../hooks";
import { OutletContext } from "../../routes";
import { useNavigate } from "../../services";
import { useLanguageStore } from "../../stores";
import { formatDuration, hasPassedExpiration } from "../../utils/datetime";
import { TopicTags } from "../CarouselCardRestyle/TopicTags.component";

export function BigCarouselCardRestyled({
  grandParentId,
  coverPublicURL,
  videoPublicURL,
  enrollType,
  expirationDate,
  id,
  isStandAlone,
  isBookmarkPage = false,
  learningObjectType,
  learningObjectTypology,
  percentageOfCompletion,
  parentId,
  simple = true,
  status,
  title,
  canAccessNow,
  credits,
  isMandatory,
  categories,
  duration,
  shortDescription,
  endDate,
  isToj,
  isHighLightEdition,
  startDate,
  isLinkRelative,
  link,
  isFavourite=false,
  attributes=[],
  labelMoreInfo,
  externalFileUrl,
  isEditorial,
  isSuggestedAI,
  isSuggestedHR,
  isSuggestedManager,
  ecm_specialization,
  ecmRegistration,
  courseId,
  rootId
  //badge,
  //certificate
  //isActive
}: BigCarouselCardProps & { simple: boolean }) {
  const { t } = useTranslation();
  const { sectionUrl } = useOutletContext<OutletContext>();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const currentPath = window.location.href.includes("per-te") ? "per-te" : "esplora";

  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleMouseEnter = () => {
    if (videoPublicURL) {
      setIsHovered(true);
      videoRef.current?.play();
    }
  };

  const handleMouseLeave = () => {
    if (videoPublicURL) {
      setIsHovered(false);
      videoRef.current?.pause();
      videoRef.current!.currentTime = 0; // rewind to start
    }
  };

  const buttonText = useButtonStatusMap({
    ecm_specialization:ecm_specialization,
    ecmRegistration:ecmRegistration,
    enrollType:enrollType,
    isCard: true,
    isSurvey:false,
    isTest:false,
    learningObjectType:learningObjectType,
    learningObjectTypology:learningObjectTypology,
    madeAttempts:0,
    percentageOfCompletion:percentageOfCompletion
  });


  const isExpired = hasPassedExpiration(expirationDate);
  const language = useLanguageStore(state => state.language)?.id;


  const {
    resumeLink,
    enrollmentMutationHadSuccess,
    addEnrollmentMutation
  } = usePlayResumeVideo({
    ecm_specialization:false,
    ecmRegistration:false,
    enrollType,
    isCard:true,
    learningObjectType,
    learningObjectTypology,
    id,
    labelMoreInfo:labelMoreInfo,
    sectionUrl,
    status,
    setOpenTojModal:undefined,
    setOpenRegistrationModal:undefined,
    triggerOpenTabs:undefined,
    linkVirtualClass:undefined,
    navigate,
    parentId,
    rootId,
    grandParentId,
    courseId:location.pathname === "/per-te"?courseId:null,
    isExpired:isExpired,
    attributesIds:attributes
  });


  const resumeLinkLinkedin = learningObjectTypology === LEVEL.DA_LTI || learningObjectTypology === LEVEL.DA_LINKEDIN ?
    `/esplora/scorm/${id}/${learningObjectTypology}?lang=${language}` :
    undefined;

  useEffect(() => {
    if (resumeLinkLinkedin){
      enrollmentMutationHadSuccess &&  window.open(resumeLinkLinkedin, "_blank", "noreferrer");
    } else {
      enrollmentMutationHadSuccess && navigate(resumeLink);
    }
  }, [
    enrollmentMutationHadSuccess,
    navigate,
    resumeLink,
    resumeLinkLinkedin
  ]);
  // questa funzione è utilizzata anche  nel componente CardCta
  function handleRedirectUrl() {
    if (isLinkRelative && externalFileUrl) {
      let cleanedLink = externalFileUrl;

      if (!cleanedLink.startsWith("/")) cleanedLink = "/" + cleanedLink;
      const regex = /\/$/; //check if url has / at the end
      if(regex.test(externalFileUrl)){
        return `${cleanedLink}${id}`; //link url and id without / in between
      }
      return `${cleanedLink}/${id}`; //link url and id with / in between
    }
    return `${externalFileUrl}`;
  }

  // funzione utilizzata al click sul Banner sia sezione per-te che esplora,
  //Nella sezione "per-te" viene utilizzata anche  sul button Primary
  const handleClickOnBanner = () => {
    //Nella sezione per-te andiamo sempre nel dettaglio
    if(isEditorial){
      const url = handleRedirectUrl();

      if (!url) return;

      if(isLinkRelative && url){
        navigate(url);
        return;
      }

      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if(location.pathname === "/per-te"){
      if((enrollType === ENROLL_TYPE.AUTO_ENROLL ||enrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL) &&
       (!status ||status === "N") &&
       !isExpired ) {
        addEnrollmentMutation();
      } else if(resumeLinkLinkedin && resumeLink.includes("guarda")){
        window.open(resumeLinkLinkedin, "_blank", "noreferrer");
      } else {
        navigate(resumeLink);
      }
    }
    // QUESTA CONDIZIONE NON DOVREBBE MAI VERIFICARSI
    // else if(!isEditorial){
    //   primaryCtaClick();
    // }
  };

  const viewDetails = () => {
    navigate.navigateToDetail({
      id,
      isForYou: currentPath === "per-te",
      learningObjectTypology,
      courseId: courseId ?? parentId,
      rootId: rootId ?? grandParentId
    });
  };

  const durationNumber = parseInt(`${duration}`);
  const showProgressBar = (!!(percentageOfCompletion && (status === STATUS_LO.P || status === STATUS_LO.C) && (learningObjectTypology === LEVEL.PATH || learningObjectTypology === LEVEL.COURSE)));

  return (
    <BigCarouselCardStyled
      id={ `banner-carousel-card-${id}` }
      onClick={ (e: React.MouseEvent<HTMLDivElement>)=> {
        e.stopPropagation();
        viewDetails();
      } }
      onMouseEnter={ handleMouseEnter }
      onMouseLeave={ handleMouseLeave }
    >
      <Box
        component="img"
        src={ coverPublicURL }
        alt={ title }
        sx={ {
          position: "absolute",
          top: 0,
          right: 0,
          width: "60%",
          height: "100%",
          objectFit: "cover",
          transition: "opacity 0.4s ease",
          opacity: isHovered ? 0 : 1,
          zIndex: 0,
          aspectRatio: "16/9"
        } }
      />
      <Box
        component="video"
        ref={ videoRef }
        src={ videoPublicURL }
        muted
        loop
        playsInline
        sx={ {
          position: "absolute",
          top: 0,
          right: 0,
          width: "60%",
          height: "100%",
          objectFit: "cover",
          transition: "opacity 0.4s ease",
          opacity: isHovered ? 1 : 0,
          zIndex: 0,
          aspectRatio: "16/9"
        } }
      />
      <StyledBoxTopLeft>
        <Box
          sx={ {
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            width: "auto"
          } }
        >
          {
            learningObjectType ?
              <CardTypeRestyle
                learningObjectType={ learningObjectType }
                learningObjectTypology={ learningObjectTypology }
                isTest={ false }
                isSurvey={ false }
                isEditorial={ !!isEditorial }
              /> :
              null
          }
          { duration && !isToj && !isNaN(durationNumber) && durationNumber > 0 ?
            <Box
              sx={ {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.25rem",
                height: "1.5rem",
                background: theme.customColors.backgroundSecondary,
                borderRadius: "0.25rem",
                border: `1px solid ${theme.customColors.textTag}`,
                padding: "0.10938rem 0.4375rem"
              } }
            >
              <Clock style={ { width: "0.875rem", height: "0.875rem" } } color={ theme.customColors.textTag } />
              <Typography
                sx={ {
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                } }
                color={ theme.customColors.textTag }
              >
                { duration && formatDuration(duration, t) }
              </Typography>
            </Box> : null }
        </Box>
        <Box sx={ {
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0
        } }>
          { /* Title overlaid on top, doesn't push below content */ }
          <ContainerTitleSubTitle
            sx={ {
              height: "auto",
              overflow: "hidden"
            } }>
            <StyledTitle
              tabIndex={ 0 }
              sx={ {
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,   // optional: limit to 3 lines
                WebkitBoxOrient: "vertical"
              } }
            >
              { title }
            </StyledTitle>
            { /* only editorial content shows description text */ }
            { isEditorial && (
              <StyledSubtitle
                tabIndex={ 0 }
                variant="h5"
                sx={ {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,   // optional: limit to 3 lines
                  WebkitBoxOrient: "vertical"
                } }
              >
                { (shortDescription||"") }
              </StyledSubtitle>
            ) }
          </ContainerTitleSubTitle>
          { /* Fixed-position content below (will not shift) */ }
          <Box
            sx={ { width: "90%", maxWidth: "672px" } }
          >
            { showProgressBar &&
              <>
                <LinearProgress
                  variant="determinate"
                  value={ Number(percentageOfCompletion) || 0 }
                  aria-label={ t("completion") }
                  sx={ {
                    height: "0.25rem",
                    borderRadius: "0.25rem",
                    mt: 1,
                    backgroundColor: theme.customColors.border,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: theme.customColors.systemPrimary01Alternative02
                    }
                  } }
                />
                <Box
                  sx={ {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: "0.5rem"
                  } }
                >
                  <Typography
                    sx={ {
                      color: theme.customColors.exploreCardText,
                      fontSize: "0.75rem",
                      fontWeight: 400
                    } }
                    variant="body2"
                  >
                    { t("completion") }
                  </Typography>
                  <Typography
                    sx={ {
                      color: theme.customColors.exploreCardText,
                      fontSize: "0.75rem",
                      fontWeight: 400
                    } }
                    variant="body2"
                  >
                    { percentageOfCompletion }%
                  </Typography>
                </Box>
              </>
            }
            <Box sx={ {
              marginY: (categories && categories?.length > 0) ? "1.5rem" : "0.5rem",
              height: (categories && categories?.length > 0) ? "1.1rem" : "0.5rem"
            } }>
              { (categories && categories?.length > 0) && <TopicTags topicTags={ categories } /> }
            </Box>
            { /*All'interno della sezione "per-te" vengono mostrati a UI i bottoni (seconda posizione)dettagli ,
         (terza posizione)aggiungi ai preferiti e (prima posizione-vecchia logica Homecard - preRestyle)Inizia/Riprendi
          Il primo bottone del componente CardCta viene nascosto*/ }
            <Box sx={ { display:"flex",gap:1 } }>
              { /* Vecchia logica presente nella HomeCard (pre-restyle) che viene mostrato solamente nella sezione "per-te"*/ }
              { (sectionUrl === "per-te") &&
                <ButtonDigited
                  typology={ "primary" }
                  buttonSize="md"
                  aria-label={ t(buttonText[status]?.label) }
                  onClick={ (e)=> {
                    e.stopPropagation();
                    e.preventDefault();
                    handleClickOnBanner();
                  } }
                  text={ isEditorial ? t("discover_more") :t(buttonText[status]?.label) }
                />
              }
              <CardCta
                isExplorePage={ true }
                canAccessNow={ canAccessNow }
                enrollType={ enrollType }
                isStandAlone={ isStandAlone }
                id={ id }
                ecm_specialization={ false }
                ecmRegistration={ false }
                learningObjectTypology={ learningObjectTypology }
                isBookmarkPage={ isBookmarkPage }
                status={ status }
                learningObjectType={ learningObjectType }
                percentageOfCompletion={ percentageOfCompletion }
                simple={ simple }
                isEditorial={ !!isEditorial }
                isCard
                grandParentId={ grandParentId }
                parentId={ parentId }
                credits={ credits }
                isLinkRelative={ isLinkRelative }
                externalFileUrl={ externalFileUrl }
                link={ link }
                showDetailsButton={ sectionUrl !== "esplora" }
                labelMoreInfo={ labelMoreInfo }
                attributesIds={ attributes }
                sx={ {
                  ">div": {
                    [theme.breakpoints.down("sm")]: {
                      gap: theme.spacing(2)
                    }
                  }
                } }/>

            </Box>
          </Box>
        </Box>
      </StyledBoxTopLeft>
      <StyledBoxTopRight>
        <StatusBoxesContainer
          id={ id }
          isMandatory={ isMandatory }
          endDate={ endDate }
          isCompleted={ status === "C" }
          isExpired={ hasPassedExpiration(expirationDate) }
          expirationDate={ expirationDate }
          startDate={ startDate }
          learningObjectTypology={ learningObjectTypology }
          isToj={ isToj }
          isHighLightEdition={ isHighLightEdition }
          isFavourite={ isFavourite }
          attributes={ attributes }
          usedInsideBigBanner={ true }
          isSuggestedHR={ isSuggestedHR }
          isSuggestedManager={ isSuggestedManager }
          isSuggestedAI={ isSuggestedAI }
        />
      </StyledBoxTopRight>
    </BigCarouselCardStyled>
  );
}

